import React, { useState, useRef } from 'react';
import { Send, Paperclip, AlertCircle, CheckCircle2, Loader2, Trash2 } from 'lucide-react';

export default function ContactForm() {
  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);
  const formRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    // Validering af antal filer (maks 5)
    if (files.length + selectedFiles.length > 5) {
      setErrorMessage('Du kan højst vedhæfte 5 filer i alt.');
      setStatus('error');
      return;
    }

    // Validering af filstørrelse og type
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    let totalSize = files.reduce((acc, f) => acc + f.size, 0);

    for (const file of selectedFiles) {
      if (!allowedTypes.includes(file.type)) {
        setErrorMessage(`Filtypen "${file.name}" er ikke tilladt. Kun PDF, PNG, JPEG, GIF og WEBP understøttes.`);
        setStatus('error');
        return;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB per fil
        setErrorMessage(`Filen "${file.name}" er for stor. Maksimal størrelse per fil er 10 MB.`);
        setStatus('error');
        return;
      }
      totalSize += file.size;
    }

    if (totalSize > 20 * 1024 * 1024) { // 20MB samlet
      setErrorMessage('Den samlede størrelse af de vedhæftede filer må ikke overstige 20 MB.');
      setStatus('error');
      return;
    }

    setFiles((prev) => [...prev, ...selectedFiles]);
    setErrorMessage('');
    if (status === 'error') setStatus('idle');
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    const formData = new FormData(e.target);
    
    // Tilføj de valgte filer til FormData (hvis der er nogen)
    // Bemærk: Hvis vi bruger standard input[type="file"] med multiple, er filerne allerede i FormData.
    // Men for at sikre fuld kontrol over vores state-baserede filer, kan vi rydde den eksisterende fil-nøgle og tilføje dem manuelt.
    formData.delete('files'); // Fjern eventuelle standard-filer
    files.forEach((file) => {
      formData.append('files', file);
    });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        body: formData, // Sender automatisk som multipart/form-data med korrekt boundary
      });

      const result = await response.json();

      if (response.ok && result.status === 'ok') {
        setStatus('success');
        setFiles([]);
        formRef.current?.reset();
      } else {
        setErrorMessage(result.message || 'Der skete en fejl under afsendelsen. Prøv venligst igen.');
        setStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setErrorMessage('Netværksfejl: Kunne ikke oprette forbindelse til serveren. Prøv igen senere.');
      setStatus('error');
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xl md:p-8">
      {status === 'success' ? (
        <div className="flex flex-col items-center justify-center text-center py-12 animate-in fade-in duration-300">
          <div className="rounded-full bg-emerald-50 p-4 text-emerald-500 mb-6">
            <CheckCircle2 className="h-16 w-16" />
          </div>
          <h3 className="font-display text-2xl font-bold text-brand-charcoal mb-3">
            Tak for din besked!
          </h3>
          <p className="text-slate-600 max-w-md leading-relaxed mb-8">
            Vi har modtaget din henvendelse og vil vende tilbage til dig så hurtigt som muligt – normalt inden for 24 timer.
          </p>
          <button
            onClick={() => setStatus('idle')}
            className="px-6 py-3 text-sm font-bold text-white bg-brand-primary hover:bg-brand-primary-dark rounded-lg transition-colors cursor-pointer"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {/* Navn */}
            <div>
              <label htmlFor="name" className="block text-sm font-bold text-brand-charcoal mb-2">
                Navn <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                placeholder="Dit fulde navn"
                className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20 transition-all"
              />
            </div>

            {/* Telefonnummer */}
            <div>
              <label htmlFor="phone" className="block text-sm font-bold text-brand-charcoal mb-2">
                Telefonnummer <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                placeholder="F.eks. 51 21 90 30"
                className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20 transition-all"
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-bold text-brand-charcoal mb-2">
              E-mailadresse <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              placeholder="din.email@eksempel.dk"
              className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20 transition-all"
            />
          </div>

          {/* Beskrivelse */}
          <div>
            <label htmlFor="message" className="block text-sm font-bold text-brand-charcoal mb-2">
              Beskrivelse af opgaven <span className="text-red-500">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              required
              rows={5}
              placeholder="Beskriv dit projekt så detaljeret som muligt (f.eks. omfang, materialer, tidsplan osv.)"
              className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20 transition-all resize-y"
            />
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-bold text-brand-charcoal mb-2">
              Vedhæft billeder eller tegninger (valgfrit)
            </label>
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-semibold text-brand-charcoal-light hover:bg-slate-100 transition-colors cursor-pointer"
                >
                  <Paperclip className="h-4 w-4 text-brand-primary" />
                  Vælg filer...
                </button>
                <span className="text-xs text-slate-500">
                  Maks. 5 filer, 10 MB pr. fil (PDF, PNG, JPEG, GIF, WEBP)
                </span>
              </div>
              
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                onChange={handleFileChange}
                className="hidden"
              />

              {/* List of selected files */}
              {files.length > 0 && (
                <ul className="divide-y divide-slate-100 rounded-lg border border-slate-100 bg-slate-50/50 p-2">
                  {files.map((file, index) => (
                    <li key={index} className="flex items-center justify-between py-2 px-3 text-xs">
                      <span className="font-medium text-slate-700 truncate max-w-[200px] sm:max-w-xs">
                        {file.name}
                      </span>
                      <div className="flex items-center gap-3">
                        <span className="text-slate-400">
                          {(file.size / (1024 * 1024)).toFixed(2)} MB
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="text-red-500 hover:text-red-700 transition-colors p-1 cursor-pointer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Antispam Honeypot Field (ABSOLUTELY HIDDEN) */}
          <div className="sr-only absolute -z-50 opacity-0 pointer-events-none" aria-hidden="true">
            <label htmlFor="website_url">Hjemmeside URL (Udfyld ikke dette felt)</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          {/* Error Message */}
          {status === 'error' && (
            <div className="flex items-start gap-3 rounded-lg bg-red-50 p-4 text-sm text-red-800 border border-red-100 animate-in shake duration-200">
              <AlertCircle className="h-5 w-5 shrink-0 text-red-600 mt-0.5" />
              <div>
                <p className="font-semibold">Der opstod en fejl</p>
                <p className="text-red-700 mt-0.5">{errorMessage}</p>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={status === 'submitting'}
            className="w-full flex items-center justify-center gap-2 rounded-lg bg-brand-primary hover:bg-brand-primary-dark px-6 py-4 text-base font-bold text-white shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {status === 'submitting' ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Sender besked...
              </>
            ) : (
              <>
                Ja tak, bliv kontaktet
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
